// FastenersTable.js - таблица крепежей в стиле SolidWorks/Excel

import React, { useState, useCallback, useMemo } from 'react';
import { Group, Line, Rect, Circle } from 'react-konva';
import { fastenerDisplayName } from './fastenersData';
import EditableText from './EditableText';
import { isFastenerDrawingKey } from '../utils/fastenerDrawing';

function fitNameFont(text, maxWidth, fontSize) {
  const width = String(text || '').length * fontSize * 0.56;
  if (width <= maxWidth) return fontSize;
  return Math.max(8, fontSize * maxWidth / width);
}

/**
 * Компонент таблицы крепежей в стиле SolidWorks
 * @param {Object} props
 * @param {Array} props.fittings - массив фитингов
 * @param {boolean} props.isLandscape - ориентация
 * @param {number} props.zoom - зум
 * @param {Object} props.tableState - состояние таблицы
 * @param {Function} props.onTableStateChange - callback изменения состояния
 * @param {Function} props.onSelect - callback выбора таблицы
 * @param {boolean} props.isSelected - выбрана ли таблица
 */
const FastenersTable = ({ 
  fittings, 
  isLandscape, 
  zoom = 1,
  tableState,
  onTableStateChange,
  onSelect,
  isSelected = false,
  onOpenDrawing,
}) => {
  // Подсчет крепежей (мемоизируем все вместе)
  const fastenerData = useMemo(() => {
    const groups = new Map();
    fittings.forEach((f) => {
      if (f.type !== 'fastener' || !f.itemKey || f.includedInPrice) return;
      const name = fastenerDisplayName(f);
      if (!groups.has(name)) {
        groups.set(name, { name, itemKey: f.itemKey, items: [] });
      }
      groups.get(name).items.push(f);
    });
    const rows = [...groups.values()];
    return {
      rows,
      keys: rows.map((row) => row.itemKey),
      hasFasteners: rows.length > 0,
    };
  }, [fittings]);

  const { rows: fastenerRows, keys: fastenerKeys, hasFasteners } = fastenerData;

  // Состояние по умолчанию (мемоизируем для оптимизации)
  const state = useMemo(() => {
    const defaultState = {
      x: isLandscape ? 77 : 54,
      y: isLandscape ? 895 : 962,
      colWidths: isLandscape ? [200, 64] : [159, 72], // ширина каждой колонки
      rowHeight: isLandscape ? 28 : 25,
      fontSize: isLandscape ? 18 : 14,
      headerHeight: isLandscape ? 30 : 28,
      // Настройки аннотации
      annotationX: isLandscape ? 90 : 90, // абсолютная координата X
      annotationY: isLandscape ? 1047 : 1060, // абсолютная координата Y
      annotationFontSize: isLandscape ? 20 : 16,
      annotationWidth: isLandscape ? 200 : 200,
      annotationText: 'Крепежи не входят в стоимость изделия*', // Текст аннотации
    };
    const merged = { ...defaultState, ...tableState };
    const firstCol = merged.colWidths?.[0];
    if (firstCol === 220 || firstCol === 240) {
      merged.colWidths = [isLandscape ? 200 : 159, merged.colWidths[1]];
    }
    return merged;
  }, [isLandscape, tableState]);

  const { x, y, colWidths, rowHeight, fontSize, headerHeight, annotationX, annotationY, annotationFontSize, annotationWidth, annotationText } = state;

  const tableWidth = colWidths.reduce((sum, w) => sum + w, 0);
  const tableHeight = headerHeight + rowHeight * fastenerKeys.length;

  // Состояния для ресайза
  const [resizingCol, setResizingCol] = useState(null);
  const [resizingRow, setResizingRow] = useState(null);
  
  // Состояние для редактируемых данных
  const [editableData, setEditableData] = useState({
    headers: ['Крепеж', 'Кол.'],
    rows: []
  });
  
  // Обновляем данные таблицы при изменении крепежей
  React.useEffect(() => {
    const rows = fastenerRows.map((row) => [row.name, `${row.items.length} шт.`]);
    
    setEditableData(prev => ({
      headers: prev.headers,
      rows: rows
    }));
  }, [fastenerRows]);

  // Обработчик начала ресайза колонки
  const handleColResizeStart = useCallback((colIndex) => {
    setResizingCol(colIndex);
  }, []);

  // Обработчик ресайза колонки
  const handleColResize = useCallback((e, colIndex) => {
    if (resizingCol !== colIndex) return;
    
    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    const localX = pointerPos.x - x;
    
    // Вычисляем новую ширину
    const prevColsWidth = colWidths.slice(0, colIndex).reduce((sum, w) => sum + w, 0);
    const newWidth = Math.max(50, localX - prevColsWidth);
    
    const newColWidths = [...colWidths];
    newColWidths[colIndex] = newWidth;
    
    if (onTableStateChange) {
      onTableStateChange({ ...state, colWidths: newColWidths });
    }
  }, [resizingCol, colWidths, state, onTableStateChange, x]);

  // Обработчик окончания ресайза
  const handleResizeEnd = useCallback(() => {
    setResizingCol(null);
    setResizingRow(null);
  }, []);

  // Обработчик изменения высоты строки
  const handleRowResize = useCallback((e) => {
    if (resizingRow === null) return;
    
    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    const localY = pointerPos.y - y - headerHeight;
    
    const newRowHeight = Math.max(20, localY / (resizingRow + 1));
    
    if (onTableStateChange) {
      onTableStateChange({ ...state, rowHeight: newRowHeight });
    }
  }, [resizingRow, state, onTableStateChange, y, headerHeight]);

  if (!hasFasteners) {
    return null;
  }

  // Вычисляем позиции колонок
  const colPositions = [0];
  for (let i = 0; i < colWidths.length; i++) {
    colPositions.push(colPositions[i] + colWidths[i]);
  }

  return (
    <Group
      onMouseMove={(e) => {
        handleColResize(e, resizingCol);
        handleRowResize(e);
      }}
      onMouseUp={handleResizeEnd}
      onMouseLeave={handleResizeEnd}
    >
      {/* Основная группа таблицы */}
      <Group
        x={x}
        y={y}
        onClick={(e) => {
          e.cancelBubble = true;
          if (onSelect) onSelect();
        }}
      >
        {/* Фон таблицы */}
        <Rect
          x={0}
          y={0}
          width={tableWidth}
          height={tableHeight}
          fill="#ffffff"
          stroke="#000000"
          strokeWidth={2}
        />

        {/* Фон заголовка */}
        <Rect
          x={0}
          y={0}
          width={tableWidth}
          height={headerHeight}
          fill="#e8e8e8"
        />

        {/* Вертикальные линии колонок */}
        {colPositions.slice(1, -1).map((pos, index) => (
          <Line
            key={`col-line-${index}`}
            points={[pos, 0, pos, tableHeight]}
            stroke="#000000"
            strokeWidth={1}
            listening={false}
          />
        ))}

        {/* Горизонтальная линия после заголовка */}
        <Line
          points={[0, headerHeight, tableWidth, headerHeight]}
          stroke="#000000"
          strokeWidth={1.5}
          listening={false}
        />

        {/* Горизонтальные линии строк */}
        {fastenerKeys.map((_, index) => (
          <Line
            key={`row-line-${index}`}
            points={[
              0, 
              headerHeight + rowHeight * (index + 1), 
              tableWidth, 
              headerHeight + rowHeight * (index + 1)
            ]}
            stroke="#cccccc"
            strokeWidth={0.5}
            listening={false}
          />
        ))}

        {/* Заголовки */}
        <EditableText
          x={10}
          y={(headerHeight - fontSize) / 2}
          text={editableData.headers[0]}
          fontSize={fontSize}
          fontFamily="Arial"
          fontStyle="bold"
          fill="#000000"
          ignoreStageScale={true}
          stageScale={zoom}
          onChange={(val) => {
            setEditableData(prev => ({
              ...prev,
              headers: [val, prev.headers[1]]
            }));
          }}
        />

        <EditableText
          x={colPositions[1] + 10}
          y={(headerHeight - fontSize) / 2}
          text={editableData.headers[1]}
          fontSize={fontSize}
          fontFamily="Arial"
          fontStyle="bold"
          fill="#000000"
          ignoreStageScale={true}
          stageScale={zoom}
          onChange={(val) => {
            setEditableData(prev => ({
              ...prev,
              headers: [prev.headers[0], val]
            }));
          }}
        />

        {/* Данные */}
        {editableData.rows.map((row, index) => {
          const rowY = headerHeight + rowHeight * index;
          const rowInfo = fastenerRows[index];
          const rowKey = rowInfo?.itemKey;
          const canDraw = isFastenerDrawingKey(rowKey);

          return (
            <Group key={index}>
              {canDraw && (
                <Group
                  x={16}
                  y={rowY + rowHeight / 2}
                  onMouseDown={(e) => { e.cancelBubble = true; }}
                  onClick={(e) => {
                    e.cancelBubble = true;
                    if (onOpenDrawing) onOpenDrawing(rowKey, rowInfo.items);
                  }}
                  onMouseEnter={(e) => {
                    const container = e.target.getStage()?.container();
                    if (container) container.style.cursor = 'pointer';
                  }}
                  onMouseLeave={(e) => {
                    const container = e.target.getStage()?.container();
                    if (container) container.style.cursor = 'default';
                  }}
                >
                  <Circle radius={8} fill="#ffffff" stroke="#333333" strokeWidth={1} />
                  <Line
                    points={[-3.5, -4, -3.5, 4, 3.5, 4, 3.5, -1.5, 1.5, -4]}
                    stroke="#333333"
                    strokeWidth={1}
                    listening={false}
                    closed
                  />
                  <Line
                    points={[-1.5, 0, 1.5, 0]}
                    stroke="#333333"
                    strokeWidth={0.8}
                    listening={false}
                  />
                </Group>
              )}
              {/* Название крепежа */}
              {(() => {
                const nameWidth = colWidths[0] - (canDraw ? 36 : 20);
                const nameFont = fitNameFont(row[0], nameWidth, fontSize - 2);
                return (
              <EditableText
                x={canDraw ? 28 : 10}
                y={rowY + (rowHeight - nameFont) / 2}
                text={row[0]}
                fontSize={nameFont}
                fontFamily="Arial"
                fill="#000000"
                width={nameWidth}
                ignoreStageScale={true}
                stageScale={zoom}
                onChange={(val) => {
                  setEditableData(prev => {
                    const newRows = [...prev.rows];
                    newRows[index] = [val, newRows[index][1]];
                    return { ...prev, rows: newRows };
                  });
                }}
              />
                );
              })()}
              {/* Количество */}
              <EditableText
                x={colPositions[1] + 10}
                y={rowY + (rowHeight - fontSize) / 2}
                text={row[1]}
                fontSize={fontSize - 2}
                fontFamily="Arial"
                fill="#000000"
                ignoreStageScale={true}
                stageScale={zoom}
                onChange={(val) => {
                  setEditableData(prev => {
                    const newRows = [...prev.rows];
                    newRows[index] = [newRows[index][0], val];
                    return { ...prev, rows: newRows };
                  });
                }}
              />
            </Group>
          );
        })}

        {/* Ручки для изменения ширины колонок */}
        {colPositions.slice(1, -1).map((pos, index) => (
          <Rect
            key={`col-resize-${index}`}
            x={pos - 3}
            y={0}
            width={6}
            height={tableHeight}
            fill="transparent"
            onMouseEnter={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = 'col-resize';
            }}
            onMouseLeave={(e) => {
              if (resizingCol === null) {
                const container = e.target.getStage().container();
                container.style.cursor = 'default';
              }
            }}
            onMouseDown={() => handleColResizeStart(index)}
          />
        ))}

        {/* Ручки для изменения высоты строк */}
        {fastenerKeys.map((_, index) => (
          <Rect
            key={`row-resize-${index}`}
            x={0}
            y={headerHeight + rowHeight * (index + 1) - 3}
            width={tableWidth}
            height={6}
            fill="transparent"
            onMouseEnter={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = 'row-resize';
            }}
            onMouseLeave={(e) => {
              if (resizingRow === null) {
                const container = e.target.getStage().container();
                container.style.cursor = 'default';
              }
            }}
            onMouseDown={() => setResizingRow(index)}
          />
        ))}

        {/* Ручка для изменения ширины правого столбца (правая граница таблицы) */}
        <Rect
          x={tableWidth - 3}
          y={0}
          width={6}
          height={tableHeight}
          fill="transparent"
          onMouseEnter={(e) => {
            const container = e.target.getStage().container();
            container.style.cursor = 'col-resize';
          }}
          onMouseLeave={(e) => {
            if (resizingCol === null) {
              const container = e.target.getStage().container();
              container.style.cursor = 'default';
            }
          }}
          onMouseDown={() => handleColResizeStart(colWidths.length - 1)}
        />

        {/* Иконка перемещения (появляется при выборе таблицы) */}
        {isSelected && (
          <Group
            x={-12}
            y={-12}
            draggable={true}
            onDragMove={(e) => {
              // Обновляем позицию таблицы в реальном времени
              const iconPos = e.target.position();
              const newX = x + iconPos.x + 12;
              const newY = y + iconPos.y + 12;
              
              if (onTableStateChange) {
                onTableStateChange({ 
                  ...state, 
                  x: newX, 
                  y: newY 
                });
              }
            }}
            onDragEnd={(e) => {
              // Сбрасываем позицию иконки обратно
              e.target.position({ x: -12, y: -12 });
            }}
            onMouseEnter={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = 'move';
            }}
            onMouseLeave={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = 'default';
            }}
          >
            {/* Фон иконки */}
            <Circle
              x={0}
              y={0}
              radius={12}
              fill="#3f51b5"
              stroke="#ffffff"
              strokeWidth={2}
            />
            {/* Крестик (4 стрелки) */}
            <Line
              points={[-6, 0, 6, 0]}
              stroke="#ffffff"
              strokeWidth={2}
              lineCap="round"
            />
            <Line
              points={[0, -6, 0, 6]}
              stroke="#ffffff"
              strokeWidth={2}
              lineCap="round"
            />
            {/* Стрелки */}
            <Line
              points={[4, -1.5, 6, 0, 4, 1.5]}
              stroke="#ffffff"
              strokeWidth={1.5}
              lineCap="round"
              lineJoin="round"
            />
            <Line
              points={[-4, -1.5, -6, 0, -4, 1.5]}
              stroke="#ffffff"
              strokeWidth={1.5}
              lineCap="round"
              lineJoin="round"
            />
            <Line
              points={[-1.5, 4, 0, 6, 1.5, 4]}
              stroke="#ffffff"
              strokeWidth={1.5}
              lineCap="round"
              lineJoin="round"
            />
            <Line
              points={[-1.5, -4, 0, -6, 1.5, -4]}
              stroke="#ffffff"
              strokeWidth={1.5}
              lineCap="round"
              lineJoin="round"
            />
          </Group>
        )}
      </Group>

      {/* Аннотация о крепежах */}
      <EditableText
        x={annotationX}
        y={annotationY}
        text={annotationText}
        fontSize={annotationFontSize}
        fontFamily="Arial"
        fontStyle="italic"
        fill="#000000"
        align="left"
        width={annotationWidth}
        wrap="word"
        ignoreStageScale={true}
        stageScale={zoom}
        onChange={(val) => {
          if (onTableStateChange) {
            onTableStateChange({ ...state, annotationText: val });
          }
        }}
      />
    </Group>
  );
};

export default FastenersTable;
